import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, of, switchMap, catchError, map, shareReplay } from 'rxjs';
import { ApiResponse, AuthUser, LoginResponse } from '../models/api.models';
import { environment } from '../../../environments/environment';

/**
 * What we know about one permission.
 *
 * `unknown` is not a synonym for `denied`. It says the session has no permission
 * data — a fact about our payloads, not about this admin's entitlements — and
 * callers are expected to treat the two differently.
 */
export type PermissionState = 'granted' | 'denied' | 'unknown';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'ho_admin_token';
  private readonly USER_KEY  = 'ho_admin_user';
  private readonly PERMS_KEY = 'ho_admin_permissions';

  currentUser = signal<AuthUser | null>(this.getStoredUser());

  /**
   * The signed-in admin's permission names, or null while unresolved.
   *
   * Nothing in the session carries these. Confirmed live: the login response
   * stores `user` as null, and `/v1/admin/profile` returns `role` as a plain
   * string plus a `role_id`, with no permissions anywhere. The names do exist —
   * `GET /v1/roles/{id}` answers with 163 of them, `platform_kpi.recalculate`
   * among them — so they are resolved from there and cached.
   *
   * The cached list renders straight away, but is re-read from the server once
   * per page load: a permission granted to the role later would otherwise stay
   * hidden until the admin signed out.
   */
  permissions = signal<string[] | null>(this.getStoredPermissions());

  private permsInFlight?: Observable<string[] | null>;
  /** True once this page load has read the role from the server. */
  private permsFresh = false;

  constructor(private http: HttpClient, private router: Router) {}

  /**
   * Resolve this session's permission names: profile → role_id → role.
   *
   * Two GETs, once per page load; a list cached by an earlier load is used
   * meanwhile, and kept if the refresh fails. Every failure resolves to null
   * rather than to an empty list, because "we could not find out" and "this
   * admin has nothing" must not render the same way — and an admin restricted
   * enough to be denied the roles endpoint is exactly who would be hurt by
   * conflating them.
   */
  loadPermissions(): Observable<string[] | null> {
    if (this.permsFresh && this.permissions() !== null) return of(this.permissions());
    if (this.permsInFlight) return this.permsInFlight;

    const base = `${environment.apiUrl}/v1`;
    this.permsInFlight = this.http.get<any>(`${base}/admin/profile`).pipe(
      switchMap(res => {
        const roleId = res?.data?.role_id;
        if (!roleId) return of(null);
        return this.http.get<any>(`${base}/roles/${roleId}`).pipe(
          map(r => {
            const role = r?.data?.role ?? r?.data;
            const names = role?.permissions;
            if (!Array.isArray(names)) return null;
            // Entries are plain strings here, unlike the /permissions catalogue,
            // which returns objects. Accept either rather than assume.
            return names.map((p: any) => (typeof p === 'string' ? p : p?.name)).filter(Boolean) as string[];
          }),
          catchError(() => of(null)),
        );
      }),
      catchError(() => of(null)),
      tap(names => {
        if (names) {
          this.permissions.set(names);
          localStorage.setItem(this.PERMS_KEY, JSON.stringify(names));
          this.permsFresh = true;
        }
        this.permsInFlight = undefined;
      }),
      // A failed refresh keeps what an earlier load cached.
      map(names => names ?? this.permissions()),
      shareReplay(1),
    );
    return this.permsInFlight;
  }

  /**
   * Exact name match, no wildcards and no role-name shortcuts.
   *
   * `platform_kpi.view` does not imply `platform_kpi.recalculate`; the API
   * separates them precisely so reading the dashboard does not imply the right
   * to queue work across every tenant database.
   */
  permissionState(name: string): PermissionState {
    const names = this.permissions();
    if (!Array.isArray(names) || names.length === 0) return 'unknown';
    return names.includes(name) ? 'granted' : 'denied';
  }

  has(name: string): boolean { return this.permissionState(name) === 'granted'; }

  login(email: string, password: string): Observable<ApiResponse<LoginResponse>> {
    const form = new FormData();
    form.append('email', email);
    form.append('password', password);
    return this.http.post<ApiResponse<LoginResponse>>(
      `${environment.apiUrl}/v1/admin/login`, form
    ).pipe(
      tap(res => {
        if (res.status && res.data?.token) {
          localStorage.setItem(this.TOKEN_KEY, res.data.token);
          // Always stringify a real object — never store undefined
          localStorage.setItem(this.USER_KEY, JSON.stringify(res.data.user ?? null));
          this.currentUser.set(res.data.user ?? null);
        }
      })
    );
  }

  logout(): Observable<any> {
    return this.http.post(`${environment.apiUrl}/v1/admin/logout`, {}).pipe(
      tap(() => this.clearSession())
    );
  }

  forgotPassword(email: string): Observable<any> {
    const form = new FormData();
    form.append('email', email);
    return this.http.post(`${environment.apiUrl}/v1/admin/forgot-password`, form);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.PERMS_KEY);
    this.currentUser.set(null);
    // Back to unknown, not to empty: the next admin's permissions are not this
    // admin's, and an empty list would read as "denied everything".
    this.permissions.set(null);
    this.permsInFlight = undefined;
    this.permsFresh = false;
    this.router.navigate(['/auth/login']);
  }

  private getStoredPermissions(): string[] | null {
    try {
      const raw = localStorage.getItem(this.PERMS_KEY);
      if (!raw || raw === 'undefined' || raw === 'null') return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length ? parsed as string[] : null;
    } catch {
      localStorage.removeItem(this.PERMS_KEY);
      return null;
    }
  }

  private getStoredUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(this.USER_KEY);
      // Guard against null, empty string, and the literal string "undefined"
      if (!raw || raw === 'undefined' || raw === 'null') return null;
      return JSON.parse(raw) as AuthUser;
    } catch {
      // Corrupt data — wipe it so it doesn't crash on every load
      localStorage.removeItem(this.USER_KEY);
      return null;
    }
  }
}
